import type { OAuthCallbackResponse } from '@saaga/shared/interfaces/connector.interface'
import { useCallback, useEffect, useState } from 'react'

import { openCenteredPopup } from './oauth-popup-window.util'
import { useOAuthChannel } from './use-oauth-channel.hook'
import { useOAuthComplete } from './use-oauth-complete.hook'
import { usePopupClosePoll } from './use-popup-close-poll.hook'

export interface UseOAuthPopupReturn {
  openOAuthPopup: (authUrl: string, provider: string) => void
  isPopupOpen: boolean
}

/**
 * Manages the OAuth popup window lifecycle.
 * Opens a centered popup, listens for BroadcastChannel callback, and completes the OAuth flow.
 * Handles error/cancel cases and detects manual popup closure via polling.
 */
export const useOAuthPopup = (
  onSuccess?: (result: OAuthCallbackResponse) => void,
  onError?: (error: unknown) => void
): UseOAuthPopupReturn => {
  const [isPopupOpen, setIsPopupOpen] = useState(false)

  const resetPopupState = useCallback(() => setIsPopupOpen(false), [])
  const { startPolling, stopPolling } = usePopupClosePoll(resetPopupState)

  const dismissPopup = useCallback(() => {
    stopPolling()
    setIsPopupOpen(false)
  }, [stopPolling])

  const { handleCallback, handleError, setProvider } = useOAuthComplete(
    onSuccess,
    onError,
    dismissPopup
  )

  useOAuthChannel(
    useCallback(
      (code: string, state: string) => {
        void handleCallback(code, state).catch((error: unknown) => onError?.(error))
      },
      [handleCallback, onError]
    ),
    handleError
  )

  useEffect(() => {
    return () => stopPolling()
  }, [stopPolling])

  const openOAuthPopup = useCallback(
    (authUrl: string, provider: string) => {
      setProvider(provider)
      const popup = openCenteredPopup(authUrl)
      if (popup) {
        setIsPopupOpen(true)
        startPolling(popup)
      }
    },
    [startPolling, setProvider]
  )

  return { openOAuthPopup, isPopupOpen }
}
